import type { ReactElement, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type HealthBannerVariant = 'info' | 'warn' | 'danger' | 'success';

export interface HealthBannerProps {
  variant: HealthBannerVariant;
  /** Ícone Tabler (default já fornecido pela variant na app) */
  icon?: ReactNode;
  title: string;
  description?: string;
  /** CTA principal (botão/link) */
  action?: ReactNode;
  /** Dismissible (renderiza X) */
  onDismiss?: () => void;
  className?: string;
}

// Padrão visual unificado com banners inline do app (ex: alerta de
// /anuncios "Mostramos apenas..."): ring violet/20 + bg violet/5, sem border
// branca currentColor. Tom de texto ink-muted pro corpo, ink no título.
const VARIANT_STYLES: Record<HealthBannerVariant, string> = {
  info: 'ring-violet/20 bg-violet/5 text-ink-muted',
  warn: 'ring-[oklch(0.74_0.15_75)]/30 bg-[oklch(0.74_0.15_75)]/[0.06] text-[oklch(0.85_0.12_75)]',
  danger:
    'ring-[oklch(0.62_0.24_25)]/35 bg-[oklch(0.62_0.24_25)]/[0.06] text-[oklch(0.82_0.15_25)]',
  success: 'ring-emerald-500/30 bg-emerald-500/5 text-emerald-300',
};

const ICON_TINT: Record<HealthBannerVariant, string> = {
  info: 'text-violet',
  warn: 'text-[oklch(0.85_0.12_75)]',
  danger: 'text-[oklch(0.82_0.15_25)]',
  success: 'text-emerald-400',
};

export function HealthBanner({
  variant,
  icon,
  title,
  description,
  action,
  onDismiss,
  className,
}: HealthBannerProps): ReactElement {
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 rounded-2xl px-5 py-4 ring-1 backdrop-blur-md',
        VARIANT_STYLES[variant],
        className,
      )}
    >
      {icon ? <span className={cn('mt-0.5 shrink-0', ICON_TINT[variant])}>{icon}</span> : null}
      <div className="flex-1">
        <p className="text-ink text-sm font-semibold tracking-tight">{title}</p>
        {description ? (
          <p className="text-ink-muted mt-1 text-sm leading-relaxed">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dispensar"
          onClick={onDismiss}
          className="text-ink-faint hover:text-ink shrink-0 transition"
        >
          {'×'}
        </button>
      ) : null}
    </div>
  );
}
